import { Box, Typography } from '@mui/material';

import { gradeName } from '../listKeys';
import type { PeGradeDetail, ScoreUnit } from '../types';
import { fmtLevel } from '../utils/drilldownFormat';
import { CELL_SX, FIRST_CELL_SX, HEAD_ROW_SX, ROW_SX, SUB_SX, TABLE_SX } from './GradeTable';
import { SmallGroupMarker } from './SmallGroupMarker';

const DOMAINS = ['listening', 'speaking', 'reading', 'writing'] as const;
const LABELS: Record<(typeof DOMAINS)[number], string> = {
  listening: 'Listening',
  speaking: 'Speaking',
  reading: 'Reading',
  writing: 'Writing',
};

export function DomainByGradeTable({
  grades,
  threshold,
  unit = 'proficiency_level',
}: {
  grades: PeGradeDetail[];
  threshold: number;
  unit?: ScoreUnit;
}) {
  return (
    <Box data-testid="domain-by-grade-table" sx={TABLE_SX}>
      {unit === 'scale_score' && (
        <Typography component="p" sx={{ ...SUB_SX, px: 2, py: 1, display: 'block' }}>
          Domain averages are in proficiency levels; the breakdowns carry no domain scale scores.
        </Typography>
      )}
      <Box sx={HEAD_ROW_SX}>
        <Box sx={FIRST_CELL_SX}>Grade</Box>
        {DOMAINS.map((d) => (
          <Box key={d} sx={CELL_SX}>
            {LABELS[d]}
          </Box>
        ))}
      </Box>
      {grades.map((g) => (
        <Box key={g.key} data-testid="domain-grade-row" sx={ROW_SX}>
          <Box sx={FIRST_CELL_SX}>
            <span>{gradeName(g.grade, g.grade_label)}</span>
            <Typography component="span" sx={SUB_SX}>
              {`${g.scored} students`}
              {g.count < threshold && <SmallGroupMarker threshold={threshold} />}
            </Typography>
          </Box>
          {DOMAINS.map((d) => {
            const row = g.domains.find((r) => r.domain === d);
            return (
              <Box key={d} sx={{ ...CELL_SX, display: 'flex', flexDirection: 'column' }}>
                <span>{fmtLevel(row?.level ?? null)}</span>
                {row?.scored !== undefined && row.scored !== g.scored && (
                  <Typography component="span" sx={SUB_SX}>{`${row.scored} scored`}</Typography>
                )}
              </Box>
            );
          })}
        </Box>
      ))}
    </Box>
  );
}
